import { ReactElement, useEffect, useState } from "react";
import { useFetch } from "../../../../../system/useFetch";
import { IDashboard, IDashboardGanttData } from "../../../types";
import { useSelector } from "react-redux";
import { ISelectedNode, IState } from "../../../../../types";
import { getTicks, useChartDimensions, useUniqueId } from "../../../../../components/charts/chartUtils";
import Chart from "../../../../../components/charts/chart";
import * as d3 from "d3";
import AxisHorizontal from "../../../../../components/charts/AxisHorizontal";
import { formatDateTimeWithSeconds, formatNumber, formatTimeWithSeconds } from "../../../../../system/utils";
import HtmlTooltip from "../../../../../components/charts/tooltip";
import KeyValueItem from "../../../../../components/charts/keyValueItem";
import { getPalette } from "../../../../../configuration/configuration";
import { Collapse, IconButton, Paper } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const pollInterval = 300000;  // 5 min


interface IProps {
  dashboard: IDashboard;
}

const getFontFactor = (dashboard: IDashboard) => {
  return dashboard.customConfig.fontSize ?? 1;
};

const ProductionGantt = ({ dashboard }: IProps): ReactElement => {
  const [open, setOpen] = useState<boolean>(false);

  const marginTop = 10;
  const marginBottomMachine = 2;
  const marginBottom = 45 * getFontFactor(dashboard);
  const machineHeight = 20 * getFontFactor(dashboard);
  const machineMargin = 20 * getFontFactor(dashboard);
  const chartID = useUniqueId("dashboard-chart");
  const selectedNode = useSelector<IState, ISelectedNode>(state => state.selectedNode);
  const [ height, setHeight ] = useState(120);
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height,
    marginBottom,
    marginLeft: 80 * getFontFactor(dashboard),
    marginRight: 9,
    marginTop
  });
  const { data: productionData } = useFetch<IDashboardGanttData | null>(
    {
      module: "dashboard",
      query: "getDashboardGantt",
      payload: {
        nodeID: selectedNode.nodeID,
        nodeType: selectedNode.type
      }
    }, pollInterval);

  useEffect(() => {
    if (productionData) {
      setHeight(productionData.machines.length * (machineHeight + machineMargin + marginBottomMachine) + marginBottom + marginTop + machineHeight);
    }
  }, [productionData, dashboard]);

  if (!productionData) {
    return (
      <div className="card-bordered-rounded " style={{ height }} />
    );
  }
  const begin = Math.min(...productionData.shiftHistory.map(s => s.beginDatetime));
  const end = Math.max(...productionData.shiftHistory.map(s => s.endDatetime));

  const xScale = d3.scaleLinear()
    .domain([begin, end])
    .range([0, dimensions.boundedWidth]);

  const pathA = `0 0 ${5 * getFontFactor(dashboard)} ${10 * getFontFactor(dashboard)} ${10 * getFontFactor(dashboard)} 0 0 0`;
  const pathB = `${5 * getFontFactor(dashboard)} 0 0 ${5 * getFontFactor(dashboard)} ${5 * getFontFactor(dashboard)} ${10 * getFontFactor(dashboard)} ${10 * getFontFactor(dashboard)} ${5 * getFontFactor(dashboard)} ${5 * getFontFactor(dashboard)} 0`;

  return (
    <Paper ref={ref} style={{ height: open ? height : "26px", fontSize: `${getFontFactor(dashboard)}em`, position: "relative"  }}>
      <div className="display-flex-row align-items-center" style={{ position: "absolute", left: 0, top: 0 }}>
        <IconButton onClick={() => setOpen(!open)} size={"small"} className="mr-1">
          {open
            ? <ExpandLessIcon fontSize="inherit" fontWeight="bold"/>
            : <ExpandMoreIcon fontSize="inherit" fontWeight="bold"/>
          }
        </IconButton>

        {!open && <strong>Gantt</strong>}
      </div>

      <Collapse in={open}>
        <Chart dimensions={dimensions}>
          <defs>
            <clipPath id="gantt-area">
              <rect x="0" y="0" width={dimensions.boundedWidth} height={dimensions.boundedHeight} />
            </clipPath>
          </defs>

          <AxisHorizontal
            chartID={chartID}
            dimensions={dimensions}
            scale={xScale}
            label={"Datetime"}
            ticks={getTicks(xScale.domain(), 3)}
            formatTick={(value, index) => index === 0 || index === 4 ? formatDateTimeWithSeconds(value as number) : formatTimeWithSeconds(value as number)}/>

          <g>
            { productionData.shiftHistory.map(sh => {
              const x = xScale(sh.beginDatetime);
              const w = Math.max(xScale(sh.endDatetime) - xScale(sh.beginDatetime) - 1, 0);

              return (
                <g key={`${chartID}-shift-${sh.shiftHistoryID}`}>
                  <rect
                    style={{ fill: sh.shiftID % 2 === 0 ? "rgba(0, 0, 0, 0.1)" : "rgba(100, 100, 100, 0.1)" }}
                    x={x}
                    y={0}
                    width={w}
                    height={dimensions.boundedHeight} />

                  <text x={x + 5} y={machineHeight - 5}
                    style={{ fill: "rgba(255, 255, 255, 0.8)",
                      fontSize: "0.9em",
                      textRendering: "optimizeLegibility"
                    }} >{sh.name}</text>
                </g>
              );
            })
            }

            { productionData.machines.map((machine, machineIndex) => {
              const machineKey = `${chartID}-${machine.machine.code}`;
              const y = (machineIndex + 1) * (machineHeight + machineMargin);

              return (
                <g key={machineKey}>
                  <text x={-dimensions.marginLeft + 5}  y={y + machineHeight / 2.0 + 2} dominantBaseline="middle" className="Axis__tick">{machine.machine.name}</text>

                  <g clipPath="url(#gantt-area)">
                    {machine.production.map((mp, mpIndex) => {
                      const x = xScale(mp.beginDatetime);
                      const w = Math.max(xScale(mp.endDatetime ?? end) - xScale(mp.beginDatetime) - 1, 0);

                      return (
                        <HtmlTooltip
                          key={`${machineKey}-prod-${mp.productionMachineID}-${mpIndex}`}
                          title={
                            <div>
                              <div className="display-flex-column align-items-center" >
                                <strong>{machine.machine.name}</strong>
                                <strong>{mp.reference}</strong>
                              </div>
                              <KeyValueItem name={"Begin"} value={formatTimeWithSeconds(mp.beginDatetime as number)} />
                              <KeyValueItem name={"End"} value={formatTimeWithSeconds(mp.endDatetime as number)} />
                            </div>
                          }>
                          <rect
                            style={{ fill: getPalette().statusColor.production.background }}
                            x={x}
                            y={y}
                            width={w}
                            height={machineHeight} />
                        </HtmlTooltip>
                      );
                    })}

                    {machine.delays.map((delay, delayIndex) => {
                      const x = xScale(delay.beginDatetime);
                      const w = Math.max(xScale(delay.endDatetime ?? end) - xScale(delay.beginDatetime) - 1, 0);

                      return (
                        <HtmlTooltip
                          key={`${machineKey}-delay-${delayIndex}`}
                          title={
                            <div>
                              <div className="display-flex-column align-items-center" >
                                <strong>{machine.machine.name}</strong>
                                <strong>Delay</strong>
                                {delay.isDownDay && <strong>Scheduled Delay</strong>}
                                <strong>{delay.productionReference}</strong>
                              </div>
                              <KeyValueItem name={"Begin"} value={formatTimeWithSeconds(delay.beginDatetime as number)} />
                              <KeyValueItem name={"End"} value={formatTimeWithSeconds(delay.endDatetime as number)} />
                            </div>
                          }>
                          <rect
                            style={{ fill: delay.isDownDay ? getPalette().statusColor.scheduleDay.background : getPalette().statusColor.delay.background }}
                            x={x}
                            y={y}
                            width={w}
                            height={machineHeight} />
                        </HtmlTooltip>
                      );
                    })}

                    {machine.production.map((mp, mpIndex) => {
                      const x = xScale(mp.beginDatetime);

                      return (
                        <text
                          key={`${machineKey}-prod-ref-${mp.productionMachineID}-${mpIndex}`}
                          x={Math.max(x, 0)} y={y + machineHeight / 2.0 + 2} dominantBaseline="middle" fontSize="0.8em"
                          fontWeight="bold" fill="whitesmoke" >
                          {x < 0 && <>&#x21a4;</>}
                          {mp.reference}
                        </text>
                      );
                    })}

                    {machine.events.map((event) => {
                      const x = xScale(event.datetime);
                      const eventKey = `${machineKey}-evt-${event.group}`;

                      if (event.type === 0) {
                        return (
                          <HtmlTooltip key={eventKey} title={
                            <div>
                              <div className="display-flex-column align-items-center" >
                                <strong>{event.name}</strong>
                                <strong>{event.reference}</strong>
                                <strong style={{ fontSize: "0.9em" }}>{event.datetime ? formatTimeWithSeconds(event.datetime) : "---"}</strong>
                              </div>
                              {event.variables.map((variable, index) => <KeyValueItem key={`${eventKey}-var-${index}`} name={variable.variable}  value={formatNumber(variable.value, 4)} /> )}
                            </div>
                          }>
                            <g transform={`translate(${x}, ${y - 10 * getFontFactor(dashboard)})`}>
                              <polygon fill={"#d84315"} points={pathA}/>
                            </g>
                          </HtmlTooltip>
                        );
                      }

                      return (
                        <HtmlTooltip key={eventKey} title={
                          <div>
                            <div className="display-flex-column align-items-center" >
                              <strong>{event.name}</strong>
                              <strong>{event.reference}</strong>
                              <strong style={{ fontSize: "0.9em" }}>{event.datetime ? formatTimeWithSeconds(event.datetime) : "---"}</strong>
                            </div>
                            {event.variables.map((variable, index) => <KeyValueItem key={`${eventKey}-var-${index}`} name={variable.variable}  value={formatNumber(variable.value, 4)} /> )}
                          </div>
                        }>
                          <g transform={`translate(${x}, ${y - 10 * getFontFactor(dashboard)})`}>
                            <polygon fill="green" points={pathB}/>
                          </g>
                        </HtmlTooltip>
                      );
                    })}
                  </g>
                </g>
              );
            })}
          </g>

        </Chart>
      </Collapse>
    </Paper>
  );
};

export default ProductionGantt;
